import React, { useEffect, useMemo, useRef, useState } from 'react';
import { jwtDecode } from 'jwt-decode';
import { EuiButton, EuiButtonEmpty, EuiLoadingSpinner, EuiSelect, EuiText } from '@elastic/eui';
import { TranslateLanguage } from './KeywordTranslator';
import EverestAudioAttribution from './EverestAudioAttribution';
import ResponsiveAudioPlayer from './ResponsiveAudioPlayer';
import { resolveChapterAudio } from './chapterAudioResolver';
import {
  createVerseTimingFile,
  loadVerseAudioTimings,
  normaliseTimings,
  saveVerseAudioTimings,
  saveVerseAudioTimingsToServer,
} from './verseAudioTimings';
import { getBibleBookFullName, parseReadingPlanReferenceList } from '../utils/scriptureReferences';
import BibleNoteDialog from './BibleNoteDialog';

const buildChapterGroups = scriptureReferences => {
  const groups = new Map();

  parseReadingPlanReferenceList(scriptureReferences).forEach(item => {
    const reference = item.reference;
    if (!reference) return;

    for (let chapter = reference.chapter; chapter <= reference.chapterEnd; chapter += 1) {
      const key = `${reference.book}:${chapter}`;
      const group = groups.get(key) || {
        key,
        book: reference.book,
        bookname: reference.bookname || getBibleBookFullName(reference.book),
        chapter,
        labels: [],
        ranges: [],
      };

      group.labels.push(item.label);
      group.ranges.push(
        reference.chapter === reference.chapterEnd && reference.verseStart !== null
          ? { start: reference.verseStart, end: reference.verseEnd }
          : null
      );
      groups.set(key, group);
    }
  });

  return Array.from(groups.values());
};

const verseIsIncluded = (verse, ranges) => (
  ranges.some(range => range === null || (verse >= range.start && verse <= range.end))
);

export default function ReadingPlanDayScripture({ scriptureReferences, language }) {
  const isAdmin = useMemo(() => {
    try {
      const token = sessionStorage.getItem('token');
      if (!token) return false;
      const decoded = jwtDecode(token);
      const role = decoded?.role
        || decoded?.['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'];
      return String(role || '').toLowerCase() === 'admin';
    } catch {
      return false;
    }
  }, []);
  const chapterGroups = useMemo(
    () => buildChapterGroups(scriptureReferences),
    [scriptureReferences]
  );
  const [chapters, setChapters] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeVerses, setActiveVerses] = useState({});
  const [noteTarget, setNoteTarget] = useState(null);
  const [selectedNoteVerse, setSelectedNoteVerse] = useState(1);
  const [currentChapterIndex, setCurrentChapterIndex] = useState(0);
  const [continuePlayback, setContinuePlayback] = useState(false);
  const [isTimingEditorOpen, setIsTimingEditorOpen] = useState(false);
  const [timingVerse, setTimingVerse] = useState('1');
  const [timingSaveStatus, setTimingSaveStatus] = useState('idle');
  const [timingSaveMessage, setTimingSaveMessage] = useState('');
  const verseElements = useRef(new Map());
  const audioElements = useRef(new Map());
  const completionGuard = useRef(false);

  const scrollToVerse = (chapterKey, verse, behavior = 'smooth') => {
    if (!chapterKey || !verse) return;
    window.requestAnimationFrame(() => {
      verseElements.current
        .get(`${chapterKey}:${verse}`)
        ?.scrollIntoView({ behavior, block: 'start' });
    });
  };

  useEffect(() => {
    let cancelled = false;
    setActiveVerses({});
    setCurrentChapterIndex(0);
    setContinuePlayback(false);
    completionGuard.current = false;

    if (!chapterGroups.length) {
      setChapters([]);
      setLoading(false);
      return () => { cancelled = true; };
    }

    setLoading(true);
    Promise.all(chapterGroups.map(async group => {
      try {
        const response = await fetch(
          `/api/bible/verses?version=unv&chineses=${encodeURIComponent(group.book)}&chap=${group.chapter}`
        );
        if (!response.ok) throw new Error(`Bible API returned ${response.status}`);

        const data = await response.json();
        const verses = (Array.isArray(data?.record) ? data.record : [])
          .map(record => ({
            verse: Number(record.sec),
            text: record.bible_text,
          }))
          .filter(record => Number.isInteger(record.verse));

        let audio = null;
        let timings = [];
        try {
          audio = await resolveChapterAudio({ book: group.book, chapter: group.chapter });
          if (audio) {
            timings = await loadVerseAudioTimings({ book: group.book, chapter: group.chapter });
          }
        } catch {
          audio = null;
        }

        return { ...group, verses, audio, timings, error: false };
      } catch {
        return { ...group, verses: [], audio: null, timings: [], error: true };
      }
    })).then(results => {
      if (!cancelled) setChapters(results);
    }).finally(() => {
      if (!cancelled) setLoading(false);
    });

    return () => { cancelled = true; };
  }, [chapterGroups]);

  useEffect(() => {
    const entries = Object.entries(activeVerses);
    const activeEntry = entries.find(([, verse]) => verse);
    if (!activeEntry) return;

    scrollToVerse(activeEntry[0], activeEntry[1]);
  }, [activeVerses]);

  useEffect(() => {
    if (!continuePlayback) return undefined;
    const chapter = chapters[currentChapterIndex];
    const audio = chapter ? audioElements.current.get(chapter.key) : null;
    if (!audio) {
      setContinuePlayback(false);
      completionGuard.current = false;
      return undefined;
    }

    const frameId = window.requestAnimationFrame(() => {
      audio.play().catch(() => {});
      setContinuePlayback(false);
      completionGuard.current = false;
    });
    return () => window.cancelAnimationFrame(frameId);
  }, [chapters, continuePlayback, currentChapterIndex]);

  const selectedChapterKey = chapters[currentChapterIndex]?.key;

  useEffect(() => {
    const current = chapters[currentChapterIndex];
    setSelectedNoteVerse(current?.verses?.find(item => verseIsIncluded(item.verse, current.ranges))?.verse || current?.verses?.[0]?.verse || 1);
    setIsTimingEditorOpen(false);
    setTimingVerse(String(current?.verses?.[0]?.verse || 1));
    setTimingSaveStatus('idle');
    setTimingSaveMessage('');
  }, [currentChapterIndex, selectedChapterKey]);

  const handleAudioPlay = chapter => {
    completionGuard.current = false;
    audioElements.current.forEach((audio, audioKey) => {
      if (audioKey !== chapter.key && !audio.paused) audio.pause();
    });

    const audio = audioElements.current.get(chapter.key);
    if (!audio) return;
    updateActiveVerse(chapter, audio.currentTime);

    const currentTiming = chapter.timings.reduce(
      (current, candidate) => candidate.start <= audio.currentTime ? candidate : current,
      null
    );
    const firstPlannedVerse = chapter.verses.find(item => verseIsIncluded(item.verse, chapter.ranges))?.verse;
    const verseToShow = currentTiming?.verse || firstPlannedVerse;
    setActiveVerses(current => ({ ...current, [chapter.key]: verseToShow || null }));
    scrollToVerse(chapter.key, verseToShow);
  };

  const finishCurrentChapter = chapter => {
    if (completionGuard.current) return;
    completionGuard.current = true;
    audioElements.current.get(chapter.key)?.pause();
    setActiveVerses(current => ({ ...current, [chapter.key]: null }));
    if (currentChapterIndex < chapters.length - 1) {
      setContinuePlayback(true);
      setCurrentChapterIndex(index => index + 1);
    }
  };

  const updateActiveVerse = (chapter, currentTime) => {
    if (!chapter.timings.length) return;
    const plannedVerses = chapter.verses.filter(item => verseIsIncluded(item.verse, chapter.ranges));
    const lastPlannedVerse = plannedVerses[plannedVerses.length - 1]?.verse;
    const endTiming = chapter.timings.find(timing => timing.verse > lastPlannedVerse);
    if (endTiming && currentTime >= endTiming.start) {
      finishCurrentChapter(chapter);
      return;
    }
    const timing = chapter.timings.reduce(
      (current, candidate) => candidate.start <= currentTime ? candidate : current,
      null
    );
    const visibleVerse = chapter.verses.some(item => item.verse === timing?.verse)
      ? timing.verse
      : null;
    setActiveVerses(current => (
      current[chapter.key] === visibleVerse ? current : { ...current, [chapter.key]: visibleVerse }
    ));
  };

  const showChapter = index => {
    setContinuePlayback(false);
    completionGuard.current = false;
    setActiveVerses({});
    setCurrentChapterIndex(index);
  };

  const positionAtFirstVerse = (chapter, audio) => {
    const firstVerse = chapter.verses.find(item => verseIsIncluded(item.verse, chapter.ranges))?.verse;
    if (!firstVerse) return;
    if (firstVerse === 1) {
      audio.currentTime = 0;
      return;
    }
    if (!chapter.timings.length) return;
    const firstTiming = chapter.timings.find(timing => timing.verse === firstVerse);
    if (firstTiming && audio.currentTime < firstTiming.start) {
      audio.currentTime = firstTiming.start;
      setActiveVerses(current => ({ ...current, [chapter.key]: firstVerse }));
    }
  };

  const replaceCurrentChapterTimings = timings => {
    setChapters(current => current.map((item, index) => (
      index === currentChapterIndex ? { ...item, timings } : item
    )));
  };

  const saveTimingDraft = timings => {
    const current = chapters[currentChapterIndex];
    if (!current) return [];
    const saved = saveVerseAudioTimings({
      book: current.book,
      chapter: current.chapter,
      timings,
    });
    replaceCurrentChapterTimings(saved);
    setTimingSaveStatus('dirty');
    setTimingSaveMessage('');
    return saved;
  };

  const markCurrentVerseTime = () => {
    const current = chapters[currentChapterIndex];
    const audio = current ? audioElements.current.get(current.key) : null;
    const verse = Number(timingVerse);
    if (!current || !audio || !Number.isInteger(verse) || verse < 1) return;

    const saved = saveTimingDraft([
      ...current.timings.filter(timing => timing.verse !== verse),
      { verse, start: Number(audio.currentTime.toFixed(3)) },
    ]);
    const nextVerse = current.verses.find(item => item.verse > verse)?.verse;
    if (nextVerse) setTimingVerse(String(nextVerse));
    setActiveVerses(values => ({ ...values, [current.key]: verse }));
    return saved;
  };

  const saveTimingsToServer = async () => {
    const current = chapters[currentChapterIndex];
    const token = sessionStorage.getItem('token');
    if (!current || !token || !current.timings.length) return;

    setTimingSaveStatus('saving');
    setTimingSaveMessage('');
    try {
      const saved = await saveVerseAudioTimingsToServer({
        book: current.book,
        chapter: current.chapter,
        timings: current.timings,
        token,
      });
      replaceCurrentChapterTimings(saved);
      setTimingSaveStatus('saved');
      setTimingSaveMessage(TranslateLanguage(language, '已保存到服務器。'));
    } catch (error) {
      setTimingSaveStatus('error');
      setTimingSaveMessage(error?.message || TranslateLanguage(language, '保存到服務器失敗。'));
    }
  };

  const downloadTimingFile = () => {
    const current = chapters[currentChapterIndex];
    if (!current) return;
    const content = createVerseTimingFile({
      book: current.book,
      chapter: current.chapter,
      timings: current.timings,
    });
    const file = new Blob([content], { type: 'application/json' });
    const url = window.URL.createObjectURL(file);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${String(current.chapter).padStart(3, '0')}.json`;
    link.click();
    window.URL.revokeObjectURL(url);
  };

  const importTimingFile = async event => {
    const [file] = Array.from(event.target.files || []);
    if (!file) return;
    try {
      saveTimingDraft(normaliseTimings(JSON.parse(await file.text())));
    } catch {
      setTimingSaveStatus('error');
      setTimingSaveMessage(TranslateLanguage(language, '標注 JSON 格式不正確。'));
    } finally {
      event.target.value = '';
    }
  };

  if (!String(scriptureReferences || '').trim()) return null;

  if (!chapterGroups.length) {
    return (
      <EuiText size="s" color="subdued">
        <p>{TranslateLanguage(language, '暫時無法識別本日經文範圍。')}</p>
      </EuiText>
    );
  }

  if (loading) {
    return (
      <div className="reading-plan-scripture-loading">
        <EuiLoadingSpinner size="m" />
        <span>{TranslateLanguage(language, '正在載入本日經文……')}</span>
      </div>
    );
  }

  const chapter = chapters[currentChapterIndex];
  if (!chapter) return null;
  const hasExplicitPlanRange = chapter.ranges.some(range => range !== null);

  return (
    <div className="reading-plan-day-scripture-content">
      {chapters.length > 1 && (
        <div className="reading-plan-chapter-pagination">
          <EuiButtonEmpty
            size="s"
            iconType="arrowLeft"
            disabled={currentChapterIndex === 0}
            onClick={() => showChapter(currentChapterIndex - 1)}
          >
            {TranslateLanguage(language, '上一章')}
          </EuiButtonEmpty>
          <strong>
            {TranslateLanguage(language, chapter.bookname)} {chapter.chapter}
            <small>{currentChapterIndex + 1} / {chapters.length}</small>
          </strong>
          <EuiButtonEmpty
            size="s"
            iconType="arrowRight"
            iconSide="right"
            disabled={currentChapterIndex >= chapters.length - 1}
            onClick={() => showChapter(currentChapterIndex + 1)}
          >
            {TranslateLanguage(language, '下一章')}
          </EuiButtonEmpty>
        </div>
      )}
      <section className="reading-plan-scripture-chapter" key={chapter.key}>
          <div className="reading-plan-scripture-chapter-heading">
            <div>
              <h4>{TranslateLanguage(language, chapter.bookname)} {chapter.chapter}</h4>
              <small>{chapter.labels.map(label => TranslateLanguage(language, label)).join('、')}</small>
            </div>
            <button
              type="button"
              className="reading-plan-chapter-note-action"
              aria-label={`${TranslateLanguage(language, '筆記')}：${TranslateLanguage(language, chapter.bookname)} ${chapter.chapter}:${selectedNoteVerse}`}
              onClick={() => {
                const selected = chapter.verses.find(item => item.verse === selectedNoteVerse) || chapter.verses[0];
                setNoteTarget({
                  book: chapter.book, bookName: chapter.bookname,
                  chapter: chapter.chapter, verse: selected?.verse || 1,
                  text: selected?.text || '', maxVerse: chapter.verses.length,
                });
              }}
            >
              {TranslateLanguage(language, '筆記')}
            </button>
            {chapter.audio && (
              <ResponsiveAudioPlayer
                ref={element => {
                  if (element) audioElements.current.set(chapter.key, element);
                  else audioElements.current.delete(chapter.key);
                }}
                className="reading-plan-chapter-audio"
                preload="metadata"
                src={chapter.audio.url}
                aria-label={`${TranslateLanguage(language, chapter.bookname)} ${chapter.chapter} ${TranslateLanguage(language, '章朗讀')}`}
                onLoadedMetadata={event => positionAtFirstVerse(chapter, event.currentTarget)}
                onPlay={() => handleAudioPlay(chapter)}
                onTimeUpdate={event => updateActiveVerse(chapter, event.currentTarget.currentTime)}
                onSeeked={event => updateActiveVerse(chapter, event.currentTarget.currentTime)}
                onEnded={() => finishCurrentChapter(chapter)}
              />
            )}
          </div>
          {chapter.audio && (
            <details className="reading-plan-audio-info">
              <summary>
                {TranslateLanguage(language, isAdmin ? '音頻信息與校準' : '音頻信息')}
              </summary>
              <div className="reading-plan-audio-info-content">
                {chapter.audio.source === 'everest' && <EverestAudioAttribution language={language} />}
                {chapter.audio.source === 'local' && (
                  <EuiText size="xs" color="subdued">
                    <p>{TranslateLanguage(language, '音頻由小牧人有聲聖經提供。')}</p>
                  </EuiText>
                )}
                {isAdmin && (
                <div className="reading-plan-audio-review">
              <div className="verse-audio-sync-status">
                <span>
                  {chapter.timings.length
                    ? TranslateLanguage(language, `已載入 ${chapter.timings.length} 節同步標注；可播放核對並修正。`)
                    : TranslateLanguage(language, '本章尚未有逐節同步標注。')}
                </span>
                <EuiButton
                  size="s"
                  display="empty"
                  onClick={() => setIsTimingEditorOpen(open => !open)}
                >
                  {TranslateLanguage(language, isTimingEditorOpen ? '完成標注' : '編輯／核對標注')}
                </EuiButton>
              </div>
              {isTimingEditorOpen && (
                <div className="verse-audio-timing-editor">
                  <EuiText size="xs">
                    {TranslateLanguage(language, '播放到該節開始處，選取節數後按「記錄目前時間」。完成後可保存到服務器。')}
                  </EuiText>
                  <div className="reading-plan-timing-row">
                    <EuiSelect
                      aria-label="選擇經文節數"
                      options={chapter.verses.map(item => ({ value: String(item.verse), text: `${item.verse}` }))}
                      value={timingVerse}
                      onChange={event => setTimingVerse(event.target.value)}
                      fullWidth
                    />
                    <EuiButton size="s" fill onClick={markCurrentVerseTime}>
                      {TranslateLanguage(language, '記錄目前時間')}
                    </EuiButton>
                  </div>
                  <div className="verse-audio-timing-actions">
                    <EuiButton
                      size="s"
                      fill
                      onClick={saveTimingsToServer}
                      disabled={!chapter.timings.length || timingSaveStatus === 'saving'}
                      isLoading={timingSaveStatus === 'saving'}
                    >
                      {TranslateLanguage(language, '保存到服務器')}
                    </EuiButton>
                    <EuiButton size="s" onClick={downloadTimingFile} disabled={!chapter.timings.length}>
                      {TranslateLanguage(language, '下載標注 JSON')}
                    </EuiButton>
                    <label className="verse-audio-import-label">
                      {TranslateLanguage(language, '匯入標注 JSON')}
                      <input type="file" accept="application/json,.json" onChange={importTimingFile} />
                    </label>
                  </div>
                  {timingSaveMessage && (
                    <EuiText size="xs" color={timingSaveStatus === 'error' ? 'danger' : 'success'}>
                      <p>{timingSaveMessage}</p>
                    </EuiText>
                  )}
                </div>
              )}
                </div>
                )}
              </div>
            </details>
          )}
          {!chapter.audio && !chapter.error && (
            <EuiText size="xs" color="subdued">
              <p>{TranslateLanguage(language, '本章暫無朗讀資源。')}</p>
            </EuiText>
          )}
          {chapter.error ? (
            <EuiText size="s" color="danger">
              <p>{TranslateLanguage(language, '本章經文載入失敗，請稍後重試。')}</p>
            </EuiText>
          ) : (
            <>
              {hasExplicitPlanRange && (
                <div className="reading-plan-range-legend">
                  <span aria-hidden="true" />
                  {TranslateLanguage(language, '黃色為本日閱讀範圍。')}
                </div>
              )}
              <div className="reading-plan-scripture-verses">
              {chapter.verses.map(verse => (
                <p
                  key={verse.verse}
                  ref={element => {
                    const key = `${chapter.key}:${verse.verse}`;
                    if (element) verseElements.current.set(key, element);
                    else verseElements.current.delete(key);
                  }}
                  className={[
                    hasExplicitPlanRange && verseIsIncluded(verse.verse, chapter.ranges) ? 'is-plan-target' : '',
                    activeVerses[chapter.key] === verse.verse ? 'is-audio-active' : '',
                    selectedNoteVerse === verse.verse ? 'is-note-selected' : '',
                  ].filter(Boolean).join(' ')}
                  role="button"
                  tabIndex={0}
                  aria-label={`${chapter.bookname} ${chapter.chapter}:${verse.verse}，點選作為筆記經節`}
                  onClick={() => setSelectedNoteVerse(verse.verse)}
                  onKeyDown={event => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      setSelectedNoteVerse(verse.verse);
                    }
                  }}
                >
                  <strong>{verse.verse}</strong>
                  <span>{TranslateLanguage(language, verse.text)}</span>
                </p>
              ))}
              </div>
            </>
          )}
      </section>
      {noteTarget && <BibleNoteDialog
        book={noteTarget.book} bookName={noteTarget.bookName} chapter={noteTarget.chapter}
        startVerse={noteTarget.verse} maxVerse={noteTarget.maxVerse}
        scriptureText={noteTarget.text}
        chapterVerses={chapter.verses}
        onClose={() => setNoteTarget(null)}
      />}
    </div>
  );
}
